import { get, set, del, createStore, keys as idbKeys } from 'idb-keyval'
import { readCurrentTenantId } from '../../utils'

export enum KnownCacheNames {
  ALL = 'all',
  NONE = 'none',
  LOCAL = 'local',
}

interface CacheEntry<T> {
  value: T
  timestamp: number
}

// 1 hour
const DEFAULT_TTL_MS = 60 * 60 * 1000

// Throttle background refreshes to once per minute per key
const REFRESH_THROTTLE_MS = 1 * 60 * 1000

const TENANT_KEY_PREFIX = 't:'

// JSON.stringify output starts with t only for `true`, so an unprefixed key is
// one written before keys carried a tenant and can never collide with a scoped one.
const isTenantScopedKey = (key: IDBValidKey): key is string =>
  typeof key === 'string' && key.startsWith(TENANT_KEY_PREFIX)

export class CacheFactory {
  private static createdCaches = new Set<string>()

  /**
   * Clear the cache registry. Primarily for testing purposes.
   */
  static clearRegistry(): void {
    this.createdCaches.clear()
  }

  static create<R, T>(
    name: string,
    enablePersistence = true,
    enableSWR = false,
    ttlMs: number = DEFAULT_TTL_MS,
    refreshThrottleMs: number = REFRESH_THROTTLE_MS,
  ): Cache<R, T> {
    // Check for duplicate cache names
    if (this.createdCaches.has(name)) {
      throw new Error(
        `Cache with name "${name}" already exists. Each cache must have a unique name.`,
      )
    }
    this.createdCaches.add(name)

    const enabledCaches = cacheConfiguration()
    if (enabledCaches[0] === KnownCacheNames.NONE) {
      return new NoOpCache<R, T>()
    }

    // Persistance is enabled by default, but allow caches to disable and
    // fall back to a local cache
    if (enabledCaches[0] === KnownCacheNames.ALL) {
      logCacheMessage(`Caching enabled for ${name}`)
      if (!enablePersistence) {
        return new LocalCache<R, T>(name, enableSWR, ttlMs, refreshThrottleMs)
      }
      return new PersistedCache<R, T>(name, enableSWR, ttlMs, refreshThrottleMs)
    }

    if (enabledCaches[0] === KnownCacheNames.LOCAL) {
      logCacheMessage(`Caching enabled for ${name}`)
      return new LocalCache<R, T>(name, enableSWR, ttlMs, refreshThrottleMs)
    }

    if (enabledCaches.includes(name)) {
      logCacheMessage(`Caching enabled for ${name}`)
      return new PersistedCache<R, T>(name, enableSWR, ttlMs, refreshThrottleMs)
    } else {
      return new NoOpCache<R, T>()
    }
  }
}

export interface Cache<R, T> {
  execute: (key: R, fn: () => Promise<T>) => Promise<T>
  invalidate: (key: R) => void
}

export class LocalCache<R, T> implements Cache<R, T> {
  protected readonly cache = new Map<string, CacheEntry<Promise<T>>>()
  protected readonly name: string
  protected readonly ttlMs: number
  protected readonly refreshThrottleMs: number
  protected readonly enableSWR: boolean
  protected readonly inFlightRefreshes = new Map<string, Promise<void>>()
  protected readonly lastRefreshTime = new Map<string, number>()

  constructor(
    name: string,
    enableSWR: boolean,
    ttlMs: number,
    refreshThrottleMs: number,
  ) {
    this.name = name
    this.enableSWR = enableSWR
    this.ttlMs = ttlMs
    this.refreshThrottleMs = refreshThrottleMs
  }

  protected isEntryExpired(entryTimestamp: number): boolean {
    return Date.now() - entryTimestamp > this.ttlMs
  }

  /**
   * One origin can serve several tenants, and they share a store. The tenant
   * belongs in the key: without it, a tenant reads what another one cached.
   */
  protected buildCacheKey(key: R): string {
    return `${TENANT_KEY_PREFIX}${readCurrentTenantId()}|${JSON.stringify(key)}`
  }

  async execute(key: R, fn: () => Promise<T>): Promise<T> {
    const cacheKey = this.buildCacheKey(key)
    const cachedEntry = this.cache.get(cacheKey)

    if (cachedEntry) {
      if (!this.isEntryExpired(cachedEntry.timestamp)) {
        logCacheMessage(`${this.name} cache hit for:`, cacheKey)

        // Stale-while-revalidate: return cached value immediately
        // but kick off a background refresh (if enabled)
        if (this.enableSWR) {
          this.refreshCacheInBackground(cacheKey, fn)
        }

        return cachedEntry.value
      }
      this.cache.delete(cacheKey)
      this.lastRefreshTime.delete(cacheKey)
    }

    const result = fn()

    // Handle rejected promises - don't cache failures
    result.catch(() => {
      this.cache.delete(cacheKey)
    })

    this.cache.set(cacheKey, {
      value: result,
      timestamp: Date.now(),
    })
    return result
  }

  protected refreshCacheInBackground(
    cacheKey: string,
    fn: () => Promise<T>,
  ): void {
    // Dedupe: if already refreshing this key, don't start another refresh
    if (this.inFlightRefreshes.has(cacheKey)) {
      logCacheMessage(`${this.name} refresh already in flight for:`, cacheKey)
      return
    }

    // Throttle: if we recently refreshed this key, don't refresh again
    const lastRefresh = this.lastRefreshTime.get(cacheKey)
    if (lastRefresh && Date.now() - lastRefresh < this.refreshThrottleMs) {
      logCacheMessage(`${this.name} refresh throttled for:`, cacheKey)
      return
    }

    // Execute the function in the background without blocking
    const refreshPromise = fn()
      .then((newValue) => {
        this.cache.set(cacheKey, {
          value: Promise.resolve(newValue),
          timestamp: Date.now(),
        })
        this.lastRefreshTime.set(cacheKey, Date.now())
        logCacheMessage(
          `${this.name} background cache refresh completed for:`,
          cacheKey,
        )
      })
      .catch((error) => {
        // Log but don't throw - background refresh failure shouldn't affect the user
        console.warn(`Background cache refresh failed for ${this.name}:`, error)
      })
      .finally(() => {
        this.inFlightRefreshes.delete(cacheKey)
      })

    this.inFlightRefreshes.set(cacheKey, refreshPromise)
  }

  invalidate(key: R) {
    const cacheKey = this.buildCacheKey(key)
    this.cache.delete(cacheKey)
    this.lastRefreshTime.delete(cacheKey)
  }
}

export class PersistedCache<R, T> extends LocalCache<R, T> {
  private readonly store: ReturnType<typeof createStore>

  constructor(
    name: string,
    enableSWR = false,
    ttlMs: number = DEFAULT_TTL_MS,
    refreshThrottleMs: number = REFRESH_THROTTLE_MS,
  ) {
    super(name, enableSWR, ttlMs, refreshThrottleMs)
    this.store = createStore(`horizon_cache_${name}`, 'keyval')
    this.loadPersistedCache()
  }

  private async loadPersistedCache(): Promise<void> {
    try {
      // Get all keys and their values from the store
      const keys = await idbKeys(this.store)
      for (const key of keys) {
        if (!isTenantScopedKey(key)) {
          // Nothing reads an unscoped record, but nothing deletes one either:
          // execute and invalidate only reach scoped keys, so it would outlive
          // its TTL for as long as the tab stays open.
          await del(key, this.store)
          continue
        }
        const entry = await get<CacheEntry<T>>(key, this.store)
        if (entry && !this.isEntryExpired(entry.timestamp)) {
          this.cache.set(key, {
            value: Promise.resolve(entry.value),
            timestamp: entry.timestamp,
          })
        } else {
          // Clean up expired entries
          await del(key, this.store)
        }
      }
    } catch (error) {
      console.warn(`Failed to load persisted cache for ${this.name}:`, error)
    }
  }

  async execute(key: R, fn: () => Promise<T>): Promise<T> {
    const cacheKey = this.buildCacheKey(key)

    // Check memory cache first
    const cachedEntry = this.cache.get(cacheKey)
    if (cachedEntry && !this.isEntryExpired(cachedEntry.timestamp)) {
      logCacheMessage(`${this.name} cache hit for:`, cacheKey)

      // Stale-while-revalidate: return cached value immediately
      // but kick off a background refresh (if enabled)
      if (this.enableSWR) {
        this.refreshPersistedCacheInBackground(cacheKey, fn)
      }

      return cachedEntry.value
    }

    // Memory cache miss, check IndexedDB
    const entry = await get<CacheEntry<T>>(cacheKey, this.store)

    if (entry && !this.isEntryExpired(entry.timestamp)) {
      logCacheMessage(`${this.name} persisted cache hit for:`, cacheKey)

      // Update memory cache
      this.cache.set(cacheKey, {
        value: Promise.resolve(entry.value),
        timestamp: entry.timestamp,
      })

      // Stale-while-revalidate: kick off background refresh for persisted cache (if enabled)
      if (this.enableSWR) {
        this.refreshPersistedCacheInBackground(cacheKey, fn)
      }

      return entry.value
    }

    // Both caches miss, execute function
    if (entry) {
      // Clean up expired entry
      try {
        await del(cacheKey, this.store)
        logCacheMessage(
          `${this.name} persisted cache invalidated for:`,
          cacheKey,
        )
      } catch (error) {
        console.warn(`Cache deletion failed for ${this.name}:`, error)
      }
    }

    const result = fn()

    // Handle rejected promises - don't cache failures
    result.catch(async () => {
      this.cache.delete(cacheKey)
      try {
        await del(cacheKey, this.store)
      } catch (error) {
        console.warn(`Cache deletion failed for ${this.name}:`, error)
      }
    })

    // Store in memory cache
    this.cache.set(cacheKey, {
      value: result,
      timestamp: Date.now(),
    })

    // Persist to IndexedDB
    try {
      const resolvedValue = await result
      await set(
        cacheKey,
        {
          value: resolvedValue,
          timestamp: Date.now(),
        },
        this.store,
      )
      return resolvedValue
    } catch (error) {
      // Clean up persisted cache on error
      try {
        await del(cacheKey, this.store)
      } catch (delError) {
        console.warn(`Cache deletion failed for ${this.name}:`, delError)
      }
      throw error
    }
  }

  private refreshPersistedCacheInBackground(
    cacheKey: string,
    fn: () => Promise<T>,
  ): void {
    // Dedupe: if already refreshing this key, don't start another refresh
    if (this.inFlightRefreshes.has(cacheKey)) {
      logCacheMessage(`${this.name} refresh already in flight for:`, cacheKey)
      return
    }

    // Throttle: if we recently refreshed this key, don't refresh again
    const lastRefresh = this.lastRefreshTime.get(cacheKey)
    if (lastRefresh && Date.now() - lastRefresh < this.refreshThrottleMs) {
      logCacheMessage(`${this.name} refresh throttled for:`, cacheKey)
      return
    }

    // Execute the function in the background without blocking
    const refreshPromise = fn()
      .then(async (newValue) => {
        const now = Date.now()

        // Update memory cache
        this.cache.set(cacheKey, {
          value: Promise.resolve(newValue),
          timestamp: now,
        })

        // Update persisted cache
        try {
          await set(
            cacheKey,
            {
              value: newValue,
              timestamp: now,
            },
            this.store,
          )
          this.lastRefreshTime.set(cacheKey, now)
          logCacheMessage(
            `${this.name} background persisted cache refresh completed for:`,
            cacheKey,
          )
        } catch (error) {
          console.warn(
            `Background persisted cache storage failed for ${this.name}:`,
            error,
          )
        }
      })
      .catch((error) => {
        // Log but don't throw - background refresh failure shouldn't affect the user
        console.warn(
          `Background persisted cache refresh failed for ${this.name}:`,
          error,
        )
      })
      .finally(() => {
        this.inFlightRefreshes.delete(cacheKey)
      })

    this.inFlightRefreshes.set(cacheKey, refreshPromise)
  }

  async invalidate(key: R) {
    super.invalidate(key)
    const cacheKey = this.buildCacheKey(key)
    await del(cacheKey, this.store).catch(console.warn)
  }
}

export class NoOpCache<R, T> implements Cache<R, T> {
  execute(_: R, fn: () => Promise<T>): Promise<T> {
    return fn()
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  invalidate(_: R): void {
    // NoOp
  }
}

function cacheConfiguration() {
  return (
    localStorage?.getItem('HORIZON_CORE_ENABLED_CACHES') || KnownCacheNames.ALL
  ).split(',')
}

function logCacheMessage(message: string, ...args: unknown[]) {
  const logging =
    localStorage.getItem('HORIZON_CORE_ENABLED_LOGGING') || 'false'
  if (logging === 'true') {
    console.log(message, args)
  }
}
